import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@wms/shared';
import { apiFetch, ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useIsPhone } from '../../lib/use-media-query';
import { useCurrentWarehouseId } from '../../lib/warehouse';
import { distributeEqually } from './alignment';
import {
  createBatchCommand,
  PartialBatchCreateCompensationFailedError,
  type Command,
} from './commands';
import { applyGeometryPatch, VersionLedger, type GeometryPatch } from './geometry-authority';
import {
  LayoutCanvas,
  clampScale,
  DEFAULT_VIEW,
  type CanvasView,
  type GeometryChange,
  type SnapMode,
} from './layout-canvas';
import { LayoutInspector } from './layout-inspector';
import { LayoutSearch } from './layout-search';
import type { LocationNode, LocationTypeItem } from '../location/types';
import {
  evictSupersededOverrides,
  reconcileLocalOverrides,
  updateSceneErrors,
} from './save-reconcile';
import type { SaveOutcome } from './save-queue';
import { sceneKey, type SceneRef } from './scene-key';
import { useCommandStack, useGeometrySaver, useSceneLease } from './use-layout-editor';
import {
  centerInside,
  isClipboardPayload,
  type ClipboardPayload,
  type Geometry,
  type LayoutObject,
  type LayoutScene,
} from './types';
import './layout-editor.css';
import { SkeletonRows } from '../skeleton';

/** 复制/粘贴/duplicate 的默认偏移，避免新对象与原对象完全重叠 */
const DUPLICATE_OFFSET_MM = 200;

/**
 * 剪贴板是攻击者可控输入，条数没有上限的话，粘贴一个巨大的数组会依次发出
 * 同样数量的 `POST /layouts/objects` 请求——既拖慢页面，真失败到一半时
 * （M-3 的补偿逻辑会把已创建的那些自动归档掉）也是一次代价很大的空跑。
 * 这里给一个跟单场景对象量级（架构书 §10.2：数百个）同一个数量级的上限，
 * 超过就直接拒绝，不做静默截断（截断会让用户以为粘贴了全部内容，其实丢了
 * 数据）（M3B 独立复审 LOW）。
 */
const MAX_PASTE_ITEMS = 200;

/**
 * 服务端 `PATCH /layouts/geometry` 的单批 `ops` 上限是 500（见
 * `apps/api/src/layout/layout.controller.ts` 的 zod schema）。一次多选群体
 * 操作（整体拖动/缩放/微调/锁定）如果超过这个数，与其让请求整批被 400 拒绝
 * ——连同这批里其它本该成功的对象一起——不如在还没提交时就明确拒绝并提示用户
 * 缩小选区（M3B 独立复审 LOW）。
 */
const MAX_GEOMETRY_OPS_PER_BATCH = 500;

class TooManyObjectsInBatchError extends Error {
  constructor(readonly count: number) {
    super('TOO_MANY_OBJECTS_IN_BATCH');
    this.name = 'TooManyObjectsInBatchError';
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

/**
 * S4 布局编辑器（M3a 基础可用 + M3b 交互/撤销重做打磨；Spec §7、架构书 §10）。
 *
 * ## M3a 独立复审 MEDIUM-1 修复
 * 待保存的几何变更队列按场景隔离（见 `save-queue.ts`），场景切换/组件卸载都
 * 会把**离开的那个场景**自己的待保存操作立即冲掉，声明正确的 `parentId`，
 * 而不是被新场景的状态污染或者干脆卸载时静默丢弃。被拒绝的操作会让 `local`
 * 乐观覆盖层回落到服务端权威几何（版本冲突除外——那个有自己的黄框 UI）。
 *
 * ## M3b 撤销/重做的场景边界（范围决定，见完成报告）
 * 命令栈**按场景清空**：离开场景时撤销历史一起清空。这不是偷懒——几何操作
 * 需要一个当时准确的 `version` 才能安全提交，而这个 version 只有"当前正在
 * 看的那个场景"才可靠。允许跨场景撤销意味着要么缓存所有历史场景的版本号
 * （容易过期出错），要么在撤销时重新拉取（体验诡异：点"撤销"却要等网络）。
 * roadmap 的验收标准（"撤销 20 步"、"300 个矩形 60fps"）本来就是单场景内的
 * 要求，这个范围决定不影响退出标准达成。
 */
export function LayoutEditorPage(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const warehouseId = useCurrentWarehouseId();

  const can = useCallback(
    (permission: string) => session?.permissions.includes(permission) ?? false,
    [session],
  );
  const canEdit = can(PERMISSIONS.LAYOUT_EDIT);
  // D-19：画布上新建位置是结构变更，额外要求 warehouse.manage（见 04-open-decisions.md）
  const canManage = can(PERMISSIONS.WAREHOUSE_MANAGE);
  const canArchive = can(PERMISSIONS.LOCATION_ARCHIVE);
  const canOverrideLease = can(PERMISSIONS.LAYOUT_LEASE_OVERRIDE);
  const canCreate = canManage; // 别名，语义更贴近下面的用途（复制/粘贴也是"创建"）

  const [parentId, setParentId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [view, setView] = useState<CanvasView>(DEFAULT_VIEW);
  const [creating, setCreating] = useState(false);
  const [snapMode, setSnapMode] = useState<SnapMode>('guides');
  const [error, setError] = useState<string | null>(null);

  /** 本地几何覆盖层：拖拽结果先在本地生效，再由 debounce 队列落库 */
  const [local, setLocal] = useState<ReadonlyMap<string, Geometry>>(new Map());
  /** 版本冲突的对象（架构书 §10.3：画布上标黄 + 两个明确动作） */
  const [conflicts, setConflicts] = useState<ReadonlyMap<string, Geometry>>(new Map());
  /**
   * 场景级"还有没有解决的保存失败" —— key 是 `sceneKey({warehouseId, parentId})`
   * （见 `scene-key.ts`）。**故意不在场景/仓库切换时重置**：切场景只重置
   * `local`/`conflicts`（那两个是"当前场景"概念），这份表要活过场景切换，
   * 直到对应 locationId 后来被成功保存一次才移出（M3B 独立复审 M-1；key 补上
   * warehouseId 见独立复审第二轮 NEW-2）。
   */
  const [sceneErrors, setSceneErrors] = useState<ReadonlyMap<string, ReadonlySet<string>>>(
    new Map(),
  );
  /** §7 红线：拖入后的非阻塞改父提示 */
  const [reparentPrompt, setReparentPrompt] = useState<{
    childId: string;
    targetId: string;
  } | null>(null);
  // 【用户裁决】画布高亮只跟随点击的那一行：黄色边框；nonce 保证同一位置
  // 连续点击不同批次行也会重新闪一次（否则"点了没反应"）
  const [searchFocus, setSearchFocus] = useState<{ id: string; nonce: number } | null>(null);
  const [newRect, setNewRect] = useState<{ x: number; y: number; w: number; h: number } | null>(
    null,
  );
  const [newName, setNewName] = useState('');

  const sceneQuery = useQuery({
    queryKey: ['layout-scene', warehouseId, parentId],
    queryFn: () => apiFetch<LayoutScene>(`/layouts${parentId ? `?parentId=${parentId}` : ''}`),
    enabled: !!warehouseId,
    retry: false,
  });
  const scene = sceneQuery.data;
  const objects = scene?.objects ?? [];

  // 【用户设计】矩形按位置类型（区域/货架/冷库…）差异化渲染
  const locationTypesQuery = useQuery({
    queryKey: ['location-types'],
    queryFn: () => apiFetch<{ items: LocationTypeItem[] }>('/location-types'),
  });
  const typeCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const type of locationTypesQuery.data?.items ?? []) map.set(type.id, type.code);
    return map;
  }, [locationTypesQuery.data]);

  /** 【找货跳转】跨场景时选中态会被场景切换 effect 清掉——存 ref 由它消费 */
  const pendingFocusRef = useRef<string | null>(null);

  /** 【用户设计】找货跳转：定位到目标位置的父场景并选中高亮 */
  const jumpToLocation = useCallback(
    async (locationId: string) => {
      try {
        const node = await apiFetch<LocationNode>(`/locations/${locationId}`);
        if (node.warehouseId !== warehouseIdRef.current) {
          setError(t('layout.searchOtherWarehouse'));
          return;
        }
        pendingFocusRef.current = locationId;
        setParentId(node.parentId);
        // 同场景跳转（parentId 不变）不会触发场景切换 effect——直接选中即可
        setSelectedIds(new Set([locationId]));
        setSearchFocus((prev) => ({ id: locationId, nonce: (prev?.nonce ?? 0) + 1 }));
      } catch {
        setError(t('empty.noResults'));
      }
    },
    [t],
  );

  // 【响应式阶段 7】<768px 画布只读（UX 书第四部分 §1 明文），且**不取编辑
  // 租约**——手机上只是看一眼的人不该抢占布局锁、把真正要编辑的桌面用户
  // 挡在门外。工具栏的编辑工具在手机档整体隐藏（不是禁用——view-first）。
  const isPhone = useIsPhone();
  const lease = useSceneLease({
    warehouseId,
    parentId,
    enabled: canEdit && !isPhone && !!warehouseId,
  });
  // 只有真正拿到租约的人才进入可编辑状态。这只是呈现层：后端在每次写入时
  // 独立判定归属，前端的 readOnly 不是安全边界（§43）。
  const editable = canEdit && !isPhone && lease.state === 'mine';

  // -------- 保持"最新值"的 ref：供命令栈里那些创建后很久才会被调用的
  // do()/undo() 闭包读取，避免捕获到创建命令那一刻就过期的渲染快照 --------
  const objectsRef = useRef(objects);
  objectsRef.current = objects;
  const localRef = useRef(local);
  localRef.current = local;
  const conflictsRef = useRef(conflicts);
  conflictsRef.current = conflicts;
  const parentIdRef = useRef(parentId);
  parentIdRef.current = parentId;
  const warehouseIdRef = useRef(warehouseId);
  warehouseIdRef.current = warehouseId;

  const commandStack = useCommandStack();

  /**
   * 每个对象最新已知的服务端版本——**唯一的版本权威**（M3B 独立复审第三轮
   * BLOCKER 1/BLOCKER 2 的结构性修复，设计说明见 `geometry-authority.ts`）。
   * 同步更新（在 onSaveSettled 的响应处理里，不等 React 渲染）；**故意不随
   * 场景/仓库切换清空**：key 是全局唯一的 locationId，max 合并保证单调安全，
   * 而"离开场景时的 flush"要在切换完成之后才为旧场景的对象解析版本，账本
   * 必须还留着它们。
   */
  const ledgerRef = useRef<VersionLedger | null>(null);
  if (!ledgerRef.current) ledgerRef.current = new VersionLedger();
  const ledger = ledgerRef.current;

  const refreshScene = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['layout-scene', warehouseIdRef.current] });
    await queryClient.invalidateQueries({ queryKey: ['locations', warehouseIdRef.current] });
  }, [queryClient]);

  const showError = useCallback(
    (err: unknown) => {
      if (err instanceof PartialBatchCreateCompensationFailedError) {
        // M3B 独立复审 M-3：不能把这个当成普通的创建失败——已经成功创建的对象
        // 连补偿归档都失败了，得让用户知道需要去位置树里手动核实/清理。
        setError(t('layout.partialCreateCompensationFailed', { count: err.attemptedCount }));
        return;
      }
      if (err instanceof TooManyObjectsInBatchError) {
        setError(t('layout.tooManyObjectsInBatch', { max: MAX_GEOMETRY_OPS_PER_BATCH }));
        return;
      }
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    },
    [t],
  );

  const geometryOf = useCallback(
    (object: LayoutObject): Geometry => local.get(object.locationId) ?? object.geometry,
    [local],
  );

  // -------- 保存队列（M3a MEDIUM-1 修复 + M3B 独立复审 H-2/M-1 修复 +
  // 独立复审第二轮 NEW-2：场景标识补上 warehouseId，见 scene-key.ts） --------
  const onSaveSettled = useCallback(
    (scene: SceneRef, outcome: SaveOutcome) => {
      // 第三轮 BLOCKER 2：**先于一切、同步地**把这批结果里的版本知识记入
      // 权威账本（成功结果的新 version、版本冲突附带的服务端 current 都算），
      // 且不做任何场景过滤——账本按 locationId 记账，跟"当前在看哪个场景"
      // 无关。此后任何时刻发出的下一个操作（哪怕在下一次 React 渲染之前）
      // 解析到的都已经是最新版本。
      ledger.noteOutcome(outcome);

      // 不管这批操作是不是当前正在看的场景，那个场景的缓存都要标脏——
      // 用户以后再回到那个场景时必须看到权威状态，不能是发起请求那一刻
      // 的乐观快照。用这批操作**自己所属**的 warehouseId（而不是
      // `warehouseIdRef.current`）失效，两者在切仓库后可能不是同一个值。
      void queryClient.invalidateQueries({
        queryKey: ['layout-scene', scene.warehouseId, scene.parentId],
      });

      // 场景级失败跟踪：不管是不是当前场景都要记，且不会因为切场景/切仓库
      // 被清掉（M3B 独立复审 M-1：旧实现在这里对非当前场景的结果直接
      // return，离开场景之后才落地的失败结果就彻底消失了）。
      setSceneErrors((prev) => updateSceneErrors(prev, scene, outcome));

      const isCurrentScene =
        scene.warehouseId === (warehouseIdRef.current ?? '') &&
        scene.parentId === parentIdRef.current;
      if (!isCurrentScene) return; // local/conflicts 只跟当前场景的乐观状态有关

      // H-2：整批请求失败时也要清理本地乐观覆盖层，让画布回落到服务端权威
      // 几何——不再像旧实现那样直接 return，把被拒绝的几何一直留在画面上。
      const next = reconcileLocalOverrides(localRef.current, conflictsRef.current, outcome);
      setLocal(next.local);
      setConflicts(next.conflicts);

      if (!outcome.ok) {
        showError(outcome.error);
        return;
      }
      const failed = outcome.results.find((r) => !r.ok && r.code !== 'COMMON.VERSION_CONFLICT');
      setError(failed && !failed.ok ? t(`errors:${failed.code}`) : null);
    },
    [queryClient, t, showError, ledger],
  );
  // MEDIUM-1 修复的全部生命周期编排都在 useGeometrySaver 内部完成（见该文件
  // 的注释）：场景/仓库切换、组件卸载都会 flush 正在离开的场景，beforeunload
  // 也挂在那里。这里只需要把当前 warehouseId/parentId 传进去。
  const saver = useGeometrySaver(warehouseId, parentId, {
    onSettled: onSaveSettled,
    // 第三轮 BLOCKER 2：PATCH 发出前一刻才解析 version，权威来源是上面的账本。
    resolveVersion: (locationId) => ledger.get(locationId),
  });
  const saverRef = useRef(saver);
  saverRef.current = saver;

  // 场景切换（或首次挂载）：本地覆盖层/冲突/撤销历史都是"相对当前场景"的
  // 概念，必须清空，否则会把上一个场景的乐观状态错当成这个场景的。
  //
  // M3B 独立复审第二轮 NEW-2：依赖数组补上 warehouseId——只依赖 parentId
  // 时，切换仓库但两边根场景都是 parentId=null 的情况完全不会触发这个
  // effect，上一个仓库的选中态/撤销历史会被错当成新仓库的。
  useEffect(() => {
    setLocal(new Map());
    setConflicts(new Map());
    // 【找货跳转】跨场景跳转时目标位置在 ref 里等着——本 effect 在 parentId
    // 落地后运行，若一律清空会把跳转刚设好的选中态抹掉；消费掉 ref 则跳转
    // 落地即选中，其余情况维持原语义（切场景清空选区）。
    const focus = pendingFocusRef.current;
    pendingFocusRef.current = null;
    setSelectedIds(focus ? new Set([focus]) : new Set());
    commandStack.clear();
    // commandStack 的方法引用是稳定的（useCommandStack 内部用 useCallback），
    // 列进依赖数组不会改变行为，这里省略只是不重复。
  }, [warehouseId, parentId]);

  /**
   * M3B 独立复审第四轮 NEW-A：权威场景数据每次落地（首次加载 / invalidate
   * 触发的重新拉取）时，清掉已被服务端**严格超越**的本地乐观覆盖——否则
   * "本标签页成功保存过 → 别的标签页/租约接管者后来又改了它"之后，重新
   * 拉取带回的更新几何会被那份过时覆盖永久遮住。清除规则的完整说明（为何
   * 严格大于、为何跳过 conflicts）见 `evictSupersededOverrides` 的注释。
   * conflicts 走 ref：这个 effect 只该由权威数据落地驱动，不该因为冲突
   * 状态变化就重跑一遍。
   */
  useEffect(() => {
    if (!scene) return;
    setLocal((prev) => evictSupersededOverrides(prev, conflictsRef.current, scene.objects));
  }, [scene]);

  /**
   * 命令栈唯一的几何落地入口：do()/undo() 都可能在很久以后才被调用，
   * 所以只读 ref，不读闭包里可能已经过期的 state。
   *
   * M3B 独立复审第三轮（BLOCKER 1/BLOCKER 2 的结构性修复，设计说明见
   * `geometry-authority.ts`）：patch 只接受几何值（`GeometryPatch`——
   * x/y/w/h/locked），**结构上不可能携带 version**：`applyGeometryPatch`
   * 逐字段显式读取、不展开 patch，就算调用方在运行时真的塞进来一个完整
   * `Geometry`（第二轮属性面板路径的事故原型），旧 version 也到不了写入
   * 路径。version 一律来自权威账本：这里只把"当前可见几何的版本"记入账本
   * （max 合并，只会推进不会回退），真正随 PATCH 发出的版本在**发送时刻**
   * 由 `resolveVersion` 从账本解析（见 useGeometrySaver 的参数）——排队和
   * 发送之间落地的任何保存结果都已经同步推进了账本。
   *
   * "版本冲突时选择用我的覆盖"（M-2）不再需要特殊通道：冲突结果落地时
   * `onSaveSettled` 已把服务端附带的 current.version 记入账本，重新提交
   * 几何值即可第一次点击就成功。
   */
  const applyGeometry = useCallback(
    (locationId: string, patch: GeometryPatch): void => {
      const obj = objectsRef.current.find((o) => o.locationId === locationId);
      if (!obj) return;
      const current = localRef.current.get(locationId) ?? obj.geometry;
      ledger.note(locationId, current.version);
      const next = applyGeometryPatch(current, patch, ledger.get(locationId));
      setLocal((prev) => new Map(prev).set(locationId, next));
      saverRef.current.queue({
        locationId,
        x: next.x,
        y: next.y,
        w: next.w,
        h: next.h,
        locked: next.locked,
      });
    },
    [ledger],
  );

  const buildGeometryCommand = useCallback(
    (changes: readonly GeometryChange[], label: string): Command => ({
      label,
      do: () => {
        // LOW（M3B 独立复审）：与其让一个超大批次被服务端整体 400 拒绝
        // ——连累这批里其它本该成功的对象——不如提交前就明确拒绝。
        if (changes.length > MAX_GEOMETRY_OPS_PER_BATCH) {
          throw new TooManyObjectsInBatchError(changes.length);
        }
        for (const c of changes) applyGeometry(c.locationId, c.after);
      },
      undo: () => {
        for (const c of changes) applyGeometry(c.locationId, c.before);
      },
    }),
    [applyGeometry],
  );

  const createLocationApi = useCallback(
    (input: {
      readonly name: string;
      readonly typeId?: string | null;
      readonly canStoreInventory: boolean;
      readonly x: number;
      readonly y: number;
      readonly w: number;
      readonly h: number;
      readonly color?: string | null;
    }) =>
      apiFetch<{ id: string; geometry: Geometry }>('/layouts/objects', {
        method: 'POST',
        body: {
          parentId: parentIdRef.current,
          name: input.name,
          typeId: input.typeId ?? undefined,
          canStoreInventory: input.canStoreInventory,
          x: input.x,
          y: input.y,
          w: input.w,
          h: input.h,
          color: input.color ?? undefined,
        },
      }),
    [],
  );

  const archiveLocationApi = useCallback(
    (id: string) =>
      apiFetch(`/locations/${id}/archive`, { method: 'POST', body: { cascade: false } }),
    [],
  );

  type CreateInput = Parameters<typeof createLocationApi>[0];

  /**
   * 创建/复制/粘贴共用：撤销 = 归档刚建的那条；重做 = 再建一条新的（补偿命令，
   * 不是回滚，见 `commands.ts` 文件头注释）。批量创建中途部分失败时的补偿
   * 归档逻辑在 `createBatchCommand` 里（M3B 独立复审 M-3），这里只负责接上
   * 页面自己的场景刷新 + 选中态更新。
   */
  const buildBatchCreateCommand = useCallback(
    (inputs: readonly CreateInput[], label: string): Command =>
      createBatchCommand({
        label,
        inputs,
        create: createLocationApi,
        archive: archiveLocationApi,
        onCreated: async (ids) => {
          await refreshScene();
          setSelectedIds(new Set(ids));
        },
        onArchived: async (ids) => {
          await refreshScene();
          setSelectedIds((prev) => {
            const next = new Set(prev);
            for (const id of ids) next.delete(id);
            return next;
          });
        },
      }),
    [createLocationApi, archiveLocationApi, refreshScene],
  );

  const runCommand = useCallback(
    (cmd: Command) => {
      void commandStack.execute(cmd).catch(showError);
    },
    [commandStack, showError],
  );

  /** 拖动/缩放结束：几何变成一条撤销命令；§7 只在单对象移动/缩放时才提示改父。 */
  const onCanvasCommit = useCallback(
    (changes: readonly GeometryChange[]) => {
      runCommand(
        buildGeometryCommand(
          changes,
          changes.length > 1 ? 'layout.cmd.moveGroup' : 'layout.cmd.move',
        ),
      );
      if (changes.length === 1) {
        const change = changes[0]!;
        const target = objects.find(
          (o) => o.locationId !== change.locationId && centerInside(change.after, geometryOf(o)),
        );
        setReparentPrompt(
          target && canManage ? { childId: change.locationId, targetId: target.locationId } : null,
        );
      } else {
        setReparentPrompt(null);
      }
    },
    [runCommand, buildGeometryCommand, objects, geometryOf, canManage],
  );

  const confirmReparent = async (): Promise<void> => {
    if (!reparentPrompt) return;
    try {
      await apiFetch(`/locations/${reparentPrompt.childId}/move`, {
        method: 'POST',
        body: { newParentId: reparentPrompt.targetId },
      });
      setReparentPrompt(null);
      setSelectedIds(new Set());
      await refreshScene();
    } catch (err) {
      setReparentPrompt(null);
      showError(err);
    }
  };

  const createRect = async (): Promise<void> => {
    if (!newRect || !newName.trim()) return;
    const input: CreateInput = {
      name: newName.trim(),
      canStoreInventory: true,
      ...newRect,
    };
    try {
      await commandStack.execute(buildBatchCreateCommand([input], 'layout.cmd.create'));
      setNewRect(null);
      setNewName('');
      setCreating(false);
    } catch (err) {
      showError(err);
    }
  };

  // -------------------------- 多选：复制/粘贴/复制(duplicate)/分布 --------------------------

  const selectedObjects = useMemo(
    () => objects.filter((o) => selectedIds.has(o.locationId)),
    [objects, selectedIds],
  );

  const duplicateSelected = useCallback(async () => {
    if (!canCreate || selectedObjects.length === 0) return;
    const inputs: CreateInput[] = selectedObjects.map((o) => {
      const g = geometryOf(o);
      return {
        name: t('layout.copyName', { name: o.name }),
        typeId: o.typeId,
        canStoreInventory: o.canStoreInventory,
        x: g.x + DUPLICATE_OFFSET_MM,
        y: g.y + DUPLICATE_OFFSET_MM,
        w: g.w,
        h: g.h,
        color: g.color,
      };
    });
    await commandStack
      .execute(buildBatchCreateCommand(inputs, 'layout.cmd.duplicate'))
      .catch(showError);
  }, [canCreate, selectedObjects, geometryOf, t, commandStack, buildBatchCreateCommand, showError]);

  const copySelected = useCallback(async () => {
    if (selectedObjects.length === 0 || !navigator.clipboard?.writeText) return;
    const payload: ClipboardPayload = {
      kind: 'wms-layout-objects',
      items: selectedObjects.map((o) => {
        const g = geometryOf(o);
        return {
          name: o.name,
          typeId: o.typeId,
          canStoreInventory: o.canStoreInventory,
          color: g.color,
          x: g.x,
          y: g.y,
          w: g.w,
          h: g.h,
        };
      }),
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload));
    } catch {
      setError(t('layout.clipboardDenied'));
    }
  }, [selectedObjects, geometryOf, t]);

  const pasteFromClipboard = useCallback(async () => {
    if (!canCreate || !navigator.clipboard?.readText) return;
    let payload: unknown;
    try {
      payload = JSON.parse(await navigator.clipboard.readText());
    } catch {
      setError(t('layout.clipboardDenied'));
      return;
    }
    // 不是我们自己的剪贴板格式：静默忽略是正确行为，不是缺陷——用户随手
    // Ctrl+V 一段无关文本不该弹错误。但如果确实是这个格式、只是条目为空，
    // 那是"想粘贴却没东西可粘"，值得给个提示而不是什么反应都没有
    // （M3B 独立复审 LOW）。
    if (!isClipboardPayload(payload)) return;
    if (payload.items.length === 0) {
      setError(t('layout.clipboardEmpty'));
      return;
    }
    if (payload.items.length > MAX_PASTE_ITEMS) {
      setError(t('layout.tooManyPasteItems', { max: MAX_PASTE_ITEMS }));
      return;
    }
    const inputs: CreateInput[] = payload.items.map((item) => ({
      name: t('layout.copyName', { name: item.name }),
      typeId: item.typeId ?? undefined,
      canStoreInventory: item.canStoreInventory,
      x: item.x + DUPLICATE_OFFSET_MM,
      y: item.y + DUPLICATE_OFFSET_MM,
      w: item.w,
      h: item.h,
      color: item.color,
    }));
    await commandStack
      .execute(buildBatchCreateCommand(inputs, 'layout.cmd.paste'))
      .catch(showError);
  }, [canCreate, t, commandStack, buildBatchCreateCommand, showError]);

  const distribute = useCallback(
    (axis: 'x' | 'y') => {
      if (selectedObjects.length < 3) return;
      const items = selectedObjects.map((o) => ({ id: o.locationId, ...geometryOf(o) }));
      const positions = distributeEqually(items, axis);
      const changes: GeometryChange[] = [];
      for (const o of selectedObjects) {
        const pos = positions.get(o.locationId);
        if (pos === undefined) continue;
        const g = geometryOf(o);
        const before = { x: g.x, y: g.y, w: g.w, h: g.h };
        const after = axis === 'x' ? { ...before, x: pos } : { ...before, y: pos };
        changes.push({ locationId: o.locationId, before, after });
      }
      if (changes.length > 0) runCommand(buildGeometryCommand(changes, 'layout.cmd.distribute'));
    },
    [selectedObjects, geometryOf, runCommand, buildGeometryCommand],
  );

  const toggleLockSelected = useCallback(() => {
    if (selectedObjects.length === 0) return;
    const allLocked = selectedObjects.every((o) => geometryOf(o).locked);
    // 锁定不改变几何坐标，走同一个 applyGeometry 但 patch 只带 locked——
    // 直接构造一条专门的命令而不是复用 buildGeometryCommand（那个只处理 x/y/w/h）。
    const cmd: Command = {
      label: 'layout.cmd.toggleLock',
      do: () => {
        if (selectedObjects.length > MAX_GEOMETRY_OPS_PER_BATCH) {
          throw new TooManyObjectsInBatchError(selectedObjects.length);
        }
        for (const o of selectedObjects) applyGeometry(o.locationId, { locked: !allLocked });
      },
      undo: () => {
        for (const o of selectedObjects) applyGeometry(o.locationId, { locked: allLocked });
      },
    };
    runCommand(cmd);
  }, [selectedObjects, geometryOf, applyGeometry, runCommand]);

  // -------------------------- 键盘操作（roadmap M3b：方向键/Tab/Esc） --------------------------
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;

      if (e.key === 'Escape') {
        setSelectedIds(new Set());
        setReparentPrompt(null);
        return;
      }
      if (e.key === 'Tab' && objects.length > 0) {
        e.preventDefault();
        const ids = objects.map((o) => o.locationId);
        const current = [...selectedIds][0];
        const idx = current ? ids.indexOf(current) : -1;
        const nextIdx = e.shiftKey ? (idx <= 0 ? ids.length - 1 : idx - 1) : (idx + 1) % ids.length;
        setSelectedIds(new Set([ids[nextIdx]!]));
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        void copySelected();
        return;
      }
      if (!editable) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        void (e.shiftKey ? commandStack.redo() : commandStack.undo()).catch(showError);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        void commandStack.redo().catch(showError);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        void duplicateSelected();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        void pasteFromClipboard();
        return;
      }
      const arrows: Record<string, readonly [number, number]> = {
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
      };
      const dir = arrows[e.key];
      if (dir && selectedIds.size > 0) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const changes: GeometryChange[] = [];
        for (const id of selectedIds) {
          const obj = objects.find((o) => o.locationId === id);
          if (!obj) continue;
          const g = geometryOf(obj);
          if (g.locked) continue;
          const before = { x: g.x, y: g.y, w: g.w, h: g.h };
          const after = { ...before, x: g.x + dir[0] * step, y: g.y + dir[1] * step };
          changes.push({ locationId: id, before, after });
        }
        if (changes.length > 0) runCommand(buildGeometryCommand(changes, 'layout.cmd.nudge'));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    editable,
    objects,
    selectedIds,
    geometryOf,
    commandStack,
    showError,
    copySelected,
    duplicateSelected,
    pasteFromClipboard,
    runCommand,
    buildGeometryCommand,
  ]);

  const onSelect = useCallback((id: string | null, opts: { readonly additive: boolean }) => {
    if (id === null) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds((prev) => {
      if (!opts.additive) return new Set([id]);
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const onMarqueeSelect = useCallback(
    (ids: readonly string[], opts: { readonly additive: boolean }) => {
      setSelectedIds((prev) => {
        if (!opts.additive) return new Set(ids);
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        return next;
      });
    },
    [],
  );

  const selected = selectedObjects.length === 1 ? selectedObjects[0]! : null;

  if (!warehouseId) return <p>{t('shell.noWarehouse')}</p>;

  const currentPending = saver.pendingCount;
  // 这里之前已经 `if (!warehouseId) return`，warehouseId 保证已定义。
  const currentSceneKey = sceneKey({ warehouseId, parentId });
  // H-2：保存状态指示只看"当前场景是否还有解决不了的保存失败"，不看通用的
  // `error` 消息——`error` 被复制/粘贴/改父/建位置等一堆不相关的操作共用，
  // 一次无关操作的成功不该把这里显示的"保存失败"悄悄冲掉（旧实现的 bug
  // 正是这样：只要最近一次 setError 是 null，状态就变回"已全部保存"，
  // 哪怕真正被拒绝的几何还画在画布上）。
  const currentSceneHasUnresolvedError = (sceneErrors.get(currentSceneKey)?.size ?? 0) > 0;
  const otherSceneErrorCount = [...sceneErrors.keys()].filter((k) => k !== currentSceneKey).length;
  // LOW（M3B 独立复审）：还有未解决的版本冲突时不能说"已全部保存"——那个
  // 对象的本地覆盖和服务端权威几何明确不一致，用户还没做出选择。
  const statusLabel = currentSceneHasUnresolvedError
    ? t('layout.saveError')
    : conflicts.size > 0
      ? t('layout.saveConflictPending', { count: conflicts.size })
      : saver.saving
        ? t('layout.saving')
        : currentPending > 0
          ? t('layout.pendingChanges', { count: currentPending })
          : t('layout.saved');

  return (
    <div className="layout-editor">
      {/* ---------------- 工具栏 ---------------- */}
      <div className="layout-toolbar">
        {!isPhone && (
          <button
            className={`btn btn--sm ${creating ? 'btn--ghost' : 'btn--primary'}`}
            onClick={() => setCreating(false)}
          >
            {t('layout.toolSelect')}
          </button>
        )}
        {canManage && !isPhone && (
          <button
            className={`btn btn--sm ${creating ? 'btn--primary' : 'btn--ghost'}`}
            disabled={!editable}
            onClick={() => setCreating(true)}
            /* 【U-04】工具名改成以"位置"为主语，绘制方式降级为 tooltip 提示 */
            title={t('layout.toolCreateHint')}
          >
            {t('layout.toolCreate')}
          </button>
        )}
        {/* 选中创建工具后，把操作方式直接摊在工具栏上——不靠 hover 才看得见
            （触摸设备没有 hover），这正是 U-04 "看不出在哪里添加东西" 的症结 */}
        {canManage && !isPhone && creating && (
          <span className="layout-toolbar__hint">{t('layout.toolCreateHint')}</span>
        )}

        {!isPhone && (<>
        <span className="layout-toolbar__sep" />

        <button
          className="btn btn--sm btn--ghost"
          disabled={!editable || !commandStack.canUndo}
          title={
            commandStack.undoLabel
              ? `${t('layout.undo')}: ${t(commandStack.undoLabel)}`
              : t('layout.undo')
          }
          onClick={() => void commandStack.undo().catch(showError)}
        >
          {t('layout.undo')}
        </button>
        <button
          className="btn btn--sm btn--ghost"
          disabled={!editable || !commandStack.canRedo}
          title={
            commandStack.redoLabel
              ? `${t('layout.redo')}: ${t(commandStack.redoLabel)}`
              : t('layout.redo')
          }
          onClick={() => void commandStack.redo().catch(showError)}
        >
          {t('layout.redo')}
        </button>

        <span className="layout-toolbar__sep" />

        <select
          className="layout-toolbar__select"
          value={snapMode}
          onChange={(e) => setSnapMode(e.target.value as SnapMode)}
          aria-label={t('layout.snapMode')}
        >
          <option value="off">{t('layout.snapOff')}</option>
          <option value="guides">{t('layout.snapGuides')}</option>
          <option value="grid">{t('layout.snapGrid')}</option>
        </select>

        <span className="layout-toolbar__sep" />
        </>)}

        {/* 面包屑：场景钻取的返回路径 */}
        <nav className="layout-breadcrumb">
          <button className="layout-breadcrumb__crumb" onClick={() => setParentId(null)}>
            {t('locations.warehouseRoot')}
          </button>
          {(scene?.breadcrumb ?? []).map((crumb) => (
            <button
              key={crumb.id}
              className="layout-breadcrumb__crumb"
              onClick={() => setParentId(crumb.id)}
            >
              <span aria-hidden="true"> › </span>
              {crumb.name}
            </button>
          ))}
        </nav>

        <span className="layout-toolbar__spacer" />
        <span className={`layout-toolbar__status ${error ? 'layout-toolbar__status--error' : ''}`}>
          {statusLabel}
        </span>
      </div>

      {/* 【阶段 7】手机只读横幅（规范原文：提示"请在更大屏幕上编辑布局"） */}
      {isPhone && <div className="layout-banner layout-banner--info">{t('layout.phoneReadOnly')}</div>}

      {/* ---------------- 租约提示条（D-07） ---------------- */}
      {lease.state === 'held' && (
        <div className="layout-banner layout-banner--warning">
          <span>
            {t('errors:LAYOUT.LEASE_HELD_BY_ANOTHER_USER', { name: lease.holder?.userName })}
          </span>
          {canOverrideLease && (
            <button
              className="btn btn--sm btn--ghost"
              onClick={() =>
                void lease.takeover().catch(() => setError(t('layout.takeoverFailed')))
              }
            >
              {t('layout.takeover')}
            </button>
          )}
        </div>
      )}
      {canEdit && lease.state === 'denied' && (
        <div className="layout-banner">{t('layout.readOnly')}</div>
      )}
      {!canEdit && <div className="layout-banner">{t('layout.readOnly')}</div>}

      {/* ---------------- §7 红线：拖入后的非阻塞改父提示 ---------------- */}
      {reparentPrompt && (
        <div className="layout-banner layout-banner--info">
          <span>
            {t('layout.reparentPrompt', {
              name: objects.find((o) => o.locationId === reparentPrompt.childId)?.name ?? '',
              target: objects.find((o) => o.locationId === reparentPrompt.targetId)?.name ?? '',
            })}
          </span>
          <button className="btn btn--sm btn--primary" onClick={() => void confirmReparent()}>
            {t('app.confirm')}
          </button>
          <button className="btn btn--sm btn--ghost" onClick={() => setReparentPrompt(null)}>
            {t('layout.keepAsIs')}
          </button>
        </div>
      )}

      {/* ---------------- 版本冲突（架构书 §10.3） ---------------- */}
      {conflicts.size > 0 && (
        <div className="layout-banner layout-banner--warning">
          <span>{t('layout.conflictBody', { count: conflicts.size })}</span>
          <button
            className="btn btn--sm btn--ghost"
            onClick={() => {
              setLocal((prev) => {
                const next = new Map(prev);
                for (const [id, server] of conflicts) next.set(id, server);
                return next;
              });
              setConflicts(new Map());
            }}
          >
            {t('layout.conflictUseServer')}
          </button>
          <button
            className="btn btn--sm btn--ghost"
            onClick={() => {
              // 第三轮：不再把 server.version 夹带在 patch 里——冲突结果落地时
              // 账本已经学到了服务端的当前版本（见 onSaveSettled 的
              // noteOutcome），这里重新提交**几何值**即可，发送时刻自然解析出
              // 那个版本，第一次点击就成功（M-2 语义保持）。
              for (const [id] of conflicts) {
                const mine = local.get(id);
                if (mine)
                  applyGeometry(id, {
                    x: mine.x,
                    y: mine.y,
                    w: mine.w,
                    h: mine.h,
                    locked: mine.locked,
                  });
              }
              setConflicts(new Map());
            }}
          >
            {t('layout.conflictUseMine')}
          </button>
        </div>
      )}

      {/*
        ---------------- 其它场景有未解决的保存失败（M3B 独立复审 M-1） ----------------
        故意不随场景切换消失——离开一个场景之后才落地的保存失败不能被"当前
        场景看起来没问题"掩盖掉，用户至少要知道这件事存在，能自己导航回去看。
      */}
      {otherSceneErrorCount > 0 && (
        <div className="layout-banner layout-banner--warning">
          <span>{t('layout.otherScenesUnsaved', { count: otherSceneErrorCount })}</span>
        </div>
      )}

      {/* ---------------- 多选工具条 ---------------- */}
      {selectedIds.size > 1 && (
        <div className="layout-banner">
          <span>{t('layout.selectionCount', { count: selectedIds.size })}</span>
          {editable && (
            <>
              <button className="btn btn--sm btn--ghost" onClick={toggleLockSelected}>
                {t('layout.lockPosition')}
              </button>
              {canCreate && (
                <button className="btn btn--sm btn--ghost" onClick={() => void duplicateSelected()}>
                  {t('layout.duplicate')}
                </button>
              )}
              {selectedIds.size >= 3 && (
                <>
                  <button className="btn btn--sm btn--ghost" onClick={() => distribute('x')}>
                    {t('layout.distributeH')}
                  </button>
                  <button className="btn btn--sm btn--ghost" onClick={() => distribute('y')}>
                    {t('layout.distributeV')}
                  </button>
                </>
              )}
            </>
          )}
          <button className="btn btn--sm btn--ghost" onClick={() => setSelectedIds(new Set())}>
            {t('layout.clearSelection')}
          </button>
        </div>
      )}

      {error && <p className="field-error">{error}</p>}

      <div className="layout-editor__body">
        {/* ---------------- 左侧场景树 ---------------- */}
        <aside className="layout-scene-list">
          {/* 【用户设计】布局找货：临期红→绿清单 + 点击跳转高亮 */}
          <h2 className="layout-scene-list__title">{t('layout.searchTitle')}</h2>
          <LayoutSearch
            onActiveChange={(active) => {
              if (!active) setSearchFocus(null);
            }}
            onJump={(id) => void jumpToLocation(id)}
          />

          <h2 className="layout-scene-list__title" style={{ marginTop: 'var(--space-3)' }}>
            {t('layout.sceneObjects')}
          </h2>
          {sceneQuery.isPending && <SkeletonRows rows={5} rowHeight={32} bars={2} />}
          {sceneQuery.isError && (
            <p className="field-error">{t('locations.warehouseUnavailable')}</p>
          )}
          {objects.map((object) => (
            <button
              key={object.locationId}
              className={`layout-scene-list__row ${
                selectedIds.has(object.locationId) ? 'layout-scene-list__row--selected' : ''
              }`}
              onClick={(e) => onSelect(object.locationId, { additive: e.shiftKey })}
              onDoubleClick={() => setParentId(object.locationId)}
            >
              {object.name}
              {object.hasChildren && <span aria-hidden="true"> ›</span>}
            </button>
          ))}
          {!sceneQuery.isPending && objects.length === 0 && (
            <p className="field-hint">{t('layout.emptyScene')}</p>
          )}
        </aside>

        {/* ---------------- 画布 ---------------- */}
        <div className="layout-editor__canvas">
          <LayoutCanvas
            objects={objects}
            geometryOf={geometryOf}
            selectedIds={selectedIds}
            conflictIds={new Set(conflicts.keys())}
            editable={editable}
            creating={creating}
            snapMode={snapMode}
            view={view}
            typeCodeById={typeCodeById}
            focus={searchFocus}
            onViewChange={setView}
            onSelect={onSelect}
            onMarqueeSelect={onMarqueeSelect}
            onDrillIn={(id) => setParentId(id)}
            onCommit={onCanvasCommit}
            onCreateRect={(rect) => setNewRect(rect)}
          />

          <div className="layout-zoom">
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => setView((v) => ({ ...v, scale: clampScale(v.scale / 1.2) }))}
            >
              −
            </button>
            <span>{Math.round(view.scale * 2000) / 10}%</span>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => setView((v) => ({ ...v, scale: clampScale(v.scale * 1.2) }))}
            >
              +
            </button>
            <button className="btn btn--ghost btn--sm" onClick={() => setView(DEFAULT_VIEW)}>
              {t('layout.fit')}
            </button>
          </div>
        </div>

        {/* ---------------- 右侧属性面板：只在单选时显示 ---------------- */}
        {selected && (
          <LayoutInspector
            object={selected}
            warehouseId={warehouseId}
            geometry={geometryOf(selected)}
            canEditGeometry={editable}
            canManage={canManage}
            canArchive={canArchive}
            onGeometryChange={(patch) => {
              // 第三轮 BLOCKER 1：属性面板现在只发几何值补丁；命令历史里的
              // before/after 也只存几何值快照（显式逐字段挑选，绝不含
              // version），并走和画布完全相同的 buildGeometryCommand 构造
              // 路径——不再有第二种"自己拼命令"的方式可以把版本快照漏进来。
              const g = geometryOf(selected);
              const before = { x: g.x, y: g.y, w: g.w, h: g.h, locked: g.locked };
              const after = { ...before, ...patch };
              if (
                before.x === after.x &&
                before.y === after.y &&
                before.w === after.w &&
                before.h === after.h &&
                before.locked === after.locked
              )
                return;
              runCommand(
                buildGeometryCommand(
                  [{ locationId: selected.locationId, before, after }],
                  'layout.cmd.editGeometry',
                ),
              );
            }}
            onStructureChanged={() => void refreshScene()}
            onClose={() => setSelectedIds(new Set())}
            onNavigate={(id) => setParentId(id)}
          />
        )}
      </div>

      {/* ---------------- 新建矩形命名 ---------------- */}
      {newRect && (
        <div className="modal-overlay" onClick={() => setNewRect(null)}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              void createRect();
            }}
          >
            <h2>{t('locations.createTitle')}</h2>
            <label className="field">
              <span>{t('locations.name')}</span>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
                required
              />
            </label>
            <p className="field-hint">{t('layout.newRectSize', { w: newRect.w, h: newRect.h })}</p>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setNewRect(null)}>
                {t('app.cancel')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={!newName.trim()}>
                {t('app.save')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
